import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Download, Loader2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lead-capture modal shown before letting the visitor download the brochure.
 * Collects first name + email, POSTs to /api/brochure/request, then triggers
 * the PDF download from the blob response.
 */
export default function BrochureLeadDialog({ open, onOpenChange }) {
  const { i18n, t } = useTranslation();
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setPrenom(''); setEmail(''); setWebsite(''); setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!prenom.trim() || !email.trim()) {
      setError(t('brochure.errRequired', 'Merci de renseigner votre prénom et votre e-mail.'));
      return;
    }
    setBusy(true);
    try {
      const lang = (i18n.language || 'fr').slice(0, 2);
      const resp = await fetch(`${API}/brochure/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prenom: prenom.trim(), email: email.trim().toLowerCase(), lang, website }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const msg = data?.detail || t('brochure.errGeneric', 'Une erreur est survenue. Merci de réessayer.');
        setError(typeof msg === 'string' ? msg : t('brochure.errGeneric', 'Une erreur est survenue.'));
        setBusy(false);
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flexi-location-brochure-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(t('brochure.errGeneric', 'Une erreur est survenue. Merci de réessayer.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md" data-testid="brochure-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#1A3C34]">
            {t('brochure.title', 'Recevez la brochure Flexi-location')}
          </DialogTitle>
          <DialogDescription>
            {t('brochure.desc', "Renseignez votre prénom et votre e-mail — nous vous envoyons la brochure PDF immédiatement.")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="brochure-prenom">{t('brochure.firstName', 'Prénom')}</Label>
            <Input
              id="brochure-prenom"
              data-testid="brochure-prenom"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              placeholder={t('brochure.firstNamePh', 'Marie')}
              autoComplete="given-name"
              maxLength={80}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="brochure-email">{t('brochure.email', 'E-mail')}</Label>
            <Input
              id="brochure-email"
              data-testid="brochure-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.be"
              autoComplete="email"
              required
            />
          </div>

          {/* Honeypot – hidden from real users */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
            aria-hidden="true"
          />

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="brochure-error">
              {error}
            </div>
          )}

          <div className="flex items-center gap-1.5 text-xs text-[#737373]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#C77D63]" />
            {t('brochure.privacy', "Nous n'envoyons aucun spam. Vos données sont utilisées uniquement pour vous transmettre la brochure et éventuelles offres.")}
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-[#C77D63] hover:bg-[#b06a51] text-white font-bold py-6"
            data-testid="brochure-submit"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('brochure.sending', 'Préparation…')}</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> {t('brochure.download', 'Télécharger la brochure')}</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
